import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "upload-modal" }
const _hoisted_2 = { class: "upload-modal__header" }
const _hoisted_3 = { class: "upload-modal__header-top" }
const _hoisted_4 = { class: "upload-step" }
const _hoisted_5 = { class: "upload-step__radio-group" }
const _hoisted_6 = { class: "upload-step" }
const _hoisted_7 = { class: "upload-modal__footer" }

import {computed, onBeforeUnmount, onMounted, ref} from "vue";
import PerfectScrollbar from "perfect-scrollbar";
import "perfect-scrollbar/css/perfect-scrollbar.css";
import UploadBox from "@/components/modules/FileUploadModal/UploadBox.vue";
import UploadCard from "@/components/modules/FileUploadModal/UploadCard.vue";
import {useFilesStore} from "@/stores/files";
import type {FileResult, FileStatus} from "@/types/files";
import {useStore} from "@/stores";

interface FileDataWithProgress {
  file_id: string;
  file_name: string;
  file_size: number;
  error: null | string;
  status?: FileStatus;
  progress?: number;
  isUploading?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'FilesUploadModal',
  emits: ["toggleShow", "showUnsavedChanges"],
  setup(__props, { emit: __emit }) {

const filesStore = useFilesStore();

const dateType = ref(true);
const isStartUpload = ref(false);
const uploadProgress = ref(0);
const isUploading = ref(false);
const uploadedBytes = ref(0);
const totalBytes = ref(0);
const isFileUploadingComplete = ref(false);

const emit = __emit;

const fileDataList = ref<FileDataWithProgress[]>([]);

const filesWithoutErrors = computed(() => {
  return fileDataList.value.filter(file => file.error === null && file.status !== "validation_error");
});

const filesWithErrors = computed(() => {
  return fileDataList.value.filter(file => {
    return file.error || file.status === "validation_error";
  });
});

const totalSize = computed(() => {
  if (isUploading.value && totalBytes.value > 0) {
    return `${formatFileSize(uploadedBytes.value)} / ${formatFileSize(totalBytes.value)}`;
  }
  // После загрузки показываем последнее известное значение totalBytes, если оно есть
  if (totalBytes.value > 0) {
    return formatFileSize(totalBytes.value);
  }
  const bytes = fileDataList.value.reduce((sum, file) => sum + file.file_size, 0);
  return formatFileSize(bytes);
});

const isSaveDisabled = computed(() => {
  // Кнопка disabled если:
  // 1. Нет файлов в списке
  if (fileDataList.value.length === 0) return true;

  // 2. Идет процесс загрузки (глобальный или индивидуальный)
  if (isUploading.value) return true;

  // 3. Проверяем, загружается ли какой-то файл индивидуально
  if (fileDataList.value.some(file => file.isUploading === true)) return true;

  // 4. Анимация завершения загрузки еще не закончилась (600 мс)
  if (isFileUploadingComplete.value) return true;

  // 5. Начата проверка файлов.
  if (isStartFileApprove.value) return true;

  // 6. Нет файлов без ошибок.
  return filesWithoutErrors.value.length === 0;
});

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " Б";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + " КБ";
  return (bytes / (1024 * 1024)).toFixed(2) + " МБ";
};

const handleRetryFile = async (fileId: string) => {
  // Создаем скрытый input для выбора файла
  const input = document.createElement("input");
  input.type = "file";
  input.accept = ".xls,.xlsx";

  input.onchange = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];

    if (!file) return;

    // Находим файл в списке
    const fileIndex = fileDataList.value.findIndex(f => f.file_id === fileId);
    if (fileIndex === -1) {
      console.error("File not found:", fileId);
      return;
    }

    // Обновляем информацию о файле и устанавливаем статус "uploading"
    fileDataList.value[fileIndex] = {
      ...fileDataList.value[fileIndex],
      file_name: file.name,
      file_size: file.size,
      status: "uploading",
      error: null,
      progress: 0,
      isUploading: true
    };

    try {
      // Загружаем один файл с отслеживанием прогресса
      const response = await filesStore.uploadFiles([file], (progressEvent) => {
        console.log("Retry file progress:", progressEvent);

        // Обновляем прогресс для конкретного файла
        if (progressEvent.loaded !== undefined && progressEvent.total && progressEvent.total > 0) {
          const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          fileDataList.value[fileIndex] = {
            ...fileDataList.value[fileIndex],
            progress: percent
          };
        }
      });

      // Обновляем данные файла после загрузки
      if (response.file_results.length > 0) {
        const result = response.file_results[0];

        // Устанавливаем финальный статус
        fileDataList.value[fileIndex] = {
          file_id: result.file_id,
          file_name: result.file_name,
          file_size: file.size,
          error: result.error ?? null,
          status: result.status as FileStatus,
          progress: 100,
          isUploading: false
        };

        // Если загрузка успешна, показываем иконку успеха на 600 мс
        if (!result.error) {
          setTimeout(() => {
            if (fileDataList.value[fileIndex]) {
              fileDataList.value[fileIndex] = {
                ...fileDataList.value[fileIndex],
                progress: undefined,
                isUploading: undefined
              };
            }
          }, 600);
        }
      }

      console.log("File retry successful:", fileId);
    } catch (error) {
      console.error("File retry error:", error);
      // Устанавливаем статус ошибки
      fileDataList.value[fileIndex] = {
        ...fileDataList.value[fileIndex],
        status: "error",
        error: "Ошибка при повторной загрузке файла",
        progress: undefined,
        isUploading: false
      };
    }
  };

  // Открываем диалог выбора файла
  input.click();
};

const handleDeleteFile = (fileId: string) => {
  console.log("Delete file:", fileId);
  fileDataList.value = fileDataList.value.filter(f => f.file_id !== fileId);
  if (fileDataList.value.length === 0) {
    isStartUpload.value = false;
  }
};

const handleCancelFile = (fileId: string) => {
  console.log("Cancel file:", fileId);
  // Логика отмены загрузки файла
};

const handleFileUpload = async (files: FileList) => {
  const filesArray = Array.from(files);

  // Запоминаем количество существующих файлов
  const existingFilesCount = fileDataList.value.length;

  // Добавляем новые файлы к существующему списку
  fileDataList.value = [
    ...fileDataList.value,
    ...filesArray.map((file, index) => ({
      file_id: `temp-${index}-${Date.now()}`,
      file_name: file.name,
      file_size: file.size,
      error: null,
      status: "pending" as const
    }))
  ];

  isStartUpload.value = true;
  isUploading.value = true;
  uploadProgress.value = 0;
  uploadedBytes.value = 0;
  totalBytes.value = 0;

  try {
    // Устанавливаем статус "uploading" только для новых файлов
    fileDataList.value.forEach((file, index) => {
      if (index >= existingFilesCount) {
        file.status = "uploading";
      }
    });

    // Загружаем файлы с отслеживанием прогресса
    const response = await filesStore.uploadFiles(filesArray, (progressEvent) => {
      // Обновляем байты для отображения
      uploadedBytes.value = progressEvent.loaded || 0;
      totalBytes.value = progressEvent.total || 0;

      // Проверяем наличие loaded и total
      if (progressEvent.loaded !== undefined) {
        if (progressEvent.total && progressEvent.total > 0) {
          // Вычисляем процент загрузки
          uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        } else {
          // Если total неизвестен, показываем индикатор активности
          uploadProgress.value = Math.min(uploadProgress.value + 5, 90);
        }
      }
    });

    // Обновляем статусы файлов на основе ответа
    response.file_results.forEach((result: FileResult) => {
      const fileIndex = fileDataList.value.findIndex(f => f.file_name === result.file_name);

      // Временное отображение ошибок валидации по файлам.
      if (result.validation_result && result.validation_result.errors.length > 0) {
        console.log(`======= Ошибка валидации файла: ${result.file_name} ======`);
        for (let error of result.validation_result.errors) {
          console.log(error);
        }
        console.log("===========================================================");
      }
      // =======

      if (fileIndex !== -1) {
        fileDataList.value[fileIndex] = {
          file_id: result.file_id,
          file_name: result.file_name,
          file_size: fileDataList.value[fileIndex].file_size,
          error: result.error ?? null,
          status: result.status as FileStatus,
          progress: 0,
          isUploading: false
        };
      }
    });

  } catch (error) {
    console.error("Upload error:", error);
    // В случае ошибки устанавливаем статус "error" только для новых файлов
    fileDataList.value.forEach((file, index) => {
      if (index >= existingFilesCount) {
        file.status = "error";
      }
    });
    uploadProgress.value = 100;
  } finally {
    isUploading.value = false;

    // Показываем иконку успеха
    isFileUploadingComplete.value = true;

    // Через 600 мс скрываем иконку успеха
    setTimeout(() => {
      isFileUploadingComplete.value = false;
    }, 600);
  }
};

// Состояние проверки файлов
const isStartFileApprove = ref(false);

const handleFilesApprove = async () => {
  isStartFileApprove.value = true;
  // Собираем ID всех файлов без ошибок
  const fileIds = fileDataList.value
    .filter(file => file.error === null && file.status !== "validation_error")
    .map(file => file.file_id);

  if (fileIds.length === 0) {
    const store = useStore();
    store.showError("Ошибка подтверждения");
    console.warn("Нет файлов для подтверждения");
    return;
  }

  try {
    // Вызываем метод подтверждения файлов из store
    await filesStore.approveFiles(fileIds);

    console.log("Файлы успешно подтверждены:", fileIds);

    // Очищаем список файлов после успешного подтверждения
    fileDataList.value = [];
    isStartUpload.value = false;

    // Закрываем модальное окно после успешного подтверждения
    toggleShow();
  } catch (error) {
    console.error("Ошибка при подтверждении файлов:", error);
  }
  isStartFileApprove.value = false;
};

const toggleShow = () => {
  if (fileDataList.value.length > 0) {
    emit("showUnsavedChanges");
  } else {
    emit("toggleShow");
  }

};

// Perfect Scrollbar
const scrollContainer = ref<HTMLElement | null>(null);
let ps: PerfectScrollbar | null = null;

onMounted(() => {
  if (scrollContainer.value) {
    ps = new PerfectScrollbar(scrollContainer.value, {
      wheelSpeed: 1,
      wheelPropagation: false,
      minScrollbarLength: 20
    });
  }
});

onBeforeUnmount(() => {
  if (ps) {
    ps.destroy();
    ps = null;
  }
});

return (_ctx: any,_cache: any) => {
  const _component_IconCross = _resolveComponent("IconCross")!
  const _component_RadioButton = _resolveComponent("RadioButton")!
  const _component_ButtonAccentSecondary = _resolveComponent("ButtonAccentSecondary")!
  const _component_ButtonAccentPrimary = _resolveComponent("ButtonAccentPrimary")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[2] || (_cache[2] = _createElementVNode("h1", { class: "upload-modal__title" }, " Загрузка данных ", -1)),
        _createElementVNode("button", {
          class: "upload-modal__button-close",
          onClick: toggleShow
        }, [
          _createVNode(_component_IconCross, { class: "upload-modal__icon-close" })
        ])
      ]),
      _cache[3] || (_cache[3] = _createElementVNode("span", { class: "upload-modal__subtitle" }, [
        _createTextVNode("Для начала работы выберите тип загружаемых данных "),
        _createElementVNode("br"),
        _createTextVNode(" и файлы для загрузки")
      ], -1))
    ]),
    _createElementVNode("div", {
      ref_key: "scrollContainer",
      ref: scrollContainer,
      class: "upload-modal__content"
    }, [
      _createElementVNode("div", _hoisted_4, [
        _cache[4] || (_cache[4] = _createElementVNode("h3", { class: "upload-step__title" }, " Шаг 1. Выберите тип данных ", -1)),
        _createElementVNode("div", _hoisted_5, [
          _createVNode(_component_RadioButton, {
            modelValue: dateType.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((dateType).value = $event)),
            "highlight-active-label": false,
            value: true,
            label: "Официальные данные"
          }, null, 8, ["modelValue"]),
          _createVNode(_component_RadioButton, {
            modelValue: dateType.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dateType).value = $event)),
            "highlight-active-label": false,
            value: false,
            label: "Оперативные данные",
            disabled: ""
          }, null, 8, ["modelValue"])
        ])
      ]),
      _createElementVNode("div", _hoisted_6, [
        _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "upload-step__title" }, " Шаг 2. Выберите и загрузите файлы ", -1)),
        _createVNode(UploadBox, {
          "accepted-formats": "XLS",
          "max-size": "25 Мб",
          onUpload: handleFileUpload
        }),
        (isStartUpload.value && filesWithoutErrors.value.length > 0)
          ? (_openBlock(), _createBlock(UploadCard, {
              key: 0,
              files: filesWithoutErrors.value,
              "total-size": totalSize.value,
              "status-text": "Загрузка завершена",
              progress: uploadProgress.value,
              "is-uploading": isUploading.value,
              "is-file-uploading-complete": isFileUploadingComplete.value,
              onRetryFile: handleRetryFile,
              onDeleteFile: handleDeleteFile,
              onCancelFile: handleCancelFile
            }, null, 8, ["files", "total-size", "progress", "is-uploading", "is-file-uploading-complete"]))
          : _createCommentVNode("", true),
        (isStartUpload.value && filesWithErrors.value.length > 0)
          ? (_openBlock(), _createBlock(UploadCard, {
              key: 1,
              files: filesWithErrors.value,
              "total-size": totalSize.value,
              "status-text": "Ошибка",
              "is-error": true,
              progress: uploadProgress.value,
              "is-uploading": isUploading.value,
              "is-file-uploading-complete": isFileUploadingComplete.value,
              onRetryFile: handleRetryFile,
              onDeleteFile: handleDeleteFile,
              onCancelFile: handleCancelFile
            }, null, 8, ["files", "total-size", "progress", "is-uploading", "is-file-uploading-complete"]))
          : _createCommentVNode("", true)
      ])
    ], 512),
    _createElementVNode("div", _hoisted_7, [
      _createVNode(_component_ButtonAccentSecondary, {
        size: "large",
        onClick: toggleShow
      }, {
        default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
          _createTextVNode(" Отмена ", -1)
        ]))]),
        _: 1
      }),
      _createVNode(_component_ButtonAccentPrimary, {
        disabled: isSaveDisabled.value,
        size: "large",
        onClick: handleFilesApprove
      }, {
        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
          _createTextVNode(" Сохранить ", -1)
        ]))]),
        _: 1
      }, 8, ["disabled"])
    ])
  ]))
}
}

})